/**
 * useProjectRisk — read-only Risk Dashboard data hook (CR 2026-09-18 §4.9).
 * See docs/phase2/change-requests/2026-09-18-project-risk-dashboard-cr.md §4.7 for the response shape.
 *
 * Point-in-time snapshot computed on request by GET /api/projects/{projectId}/risk.
 * Hits the GoverningEvents API (separate API Gateway, same Cognito auth) via
 * useGoverningEventsClient — the same client the governing-events UI already uses.
 */

import { useQuery } from '@tanstack/react-query';

import { useGoverningEventsClient } from '@/lib/api';

export type RiskLevel = 'green' | 'yellow' | 'red';

export interface RiskSignal {
  level: RiskLevel;
  reason: string;
  metrics: Record<string, unknown>;
}

export interface MeetingHealth {
  meeting_id: string;
  meeting_name: string;
  meeting_date: string | null;
  sentiment: 'positive' | 'neutral' | 'negative' | 'at_risk' | null;
  topics_covered_pct: number | null;
  topics_missing: string[];
  /**
   * Avoma recording/details URL (`project_meetings.avoma_link`), or null when no recording has been
   * linked yet.
   *
   * Supplied by `packages/gates/handlers/get-project-risk.ts` (meeting-health query selects
   * `pm.avoma_link`). Kept optional so the UI stays resilient to responses from older backend
   * deployments that predate the field — `RiskDashboard` renders the link only when it is present.
   */
  avoma_link?: string | null;
}

export interface EscalationSummary {
  id: number;
  raised_date: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  raised_by: string;
  status: 'open' | 'resolved';
}

export interface ProjectRiskResponse {
  project_id: string;
  current_phase: string;
  computed_at: string;
  overall_level: RiskLevel;
  signals: {
    schedule: RiskSignal;
    budget: RiskSignal;
    scope: RiskSignal;
    quality: RiskSignal;
  };
  meeting_health: MeetingHealth[];
  last_meeting_at_risk: boolean;
  phase_timeline: {
    current_phase: string;
    days_in_phase: number;
    planned_kickoff_date: string | null;
    expected_completion_date: string | null;
    expected_completion_overdue: boolean;
  };
  escalations: EscalationSummary[];
}

export function useProjectRisk(projectId: string) {
  const client = useGoverningEventsClient();
  const query = useQuery({
    queryKey: ['project-risk', projectId],
    queryFn: async (): Promise<ProjectRiskResponse> => {
      const res = await client.get<ProjectRiskResponse>(`/api/projects/${projectId}/risk`);
      return res.data;
    },
    enabled: !!projectId,
  });
  return {
    risk: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
